import { PERMISSIONS, PermissionKey } from "./permissions";
import { Role } from "./roles";

/**
 * Keys that existed before the 2026-10 RBAC consolidation. Kept as an
 * explicit, separately-named list (rather than deriving from PERMISSIONS
 * directly) so the legacy "every permission except one" shortcut the
 * original rolePermissions.js used can be reproduced exactly, without
 * silently pulling in any of the new keys added by this consolidation.
 */
const LEGACY_PERMISSION_KEYS: PermissionKey[] = [
  PERMISSIONS.WORKLIST_ROUTE,
  PERMISSIONS.PATIENT_ROUTE,
  PERMISSIONS.REPORT_ROUTE,
  PERMISSIONS.TRANSFER_WOUND_ASSESSMENT_ROUTE,
  PERMISSIONS.MERGE_WOUND_ASSESSMENT_ROUTE,
  PERMISSIONS.ADMIN_ROUTE,
  PERMISSIONS.PATIENT,
  PERMISSIONS.ADD_PATIENT,
  PERMISSIONS.ASSESSMENT_THREE_DOT_MENU,
  PERMISSIONS.ASSESSMENT,
  PERMISSIONS.CHARACTERISTIC,
  PERMISSIONS.MEASUREMENT,
  PERMISSIONS.MEASUREMENT_BUTTON,
  PERMISSIONS.TREATMENT,
  PERMISSIONS.TREATMENT_SEARCH,
  PERMISSIONS.TREATMENT_ADD_INTERVENTION,
  PERMISSIONS.NOTES,
  PERMISSIONS.CUSTOM_LABEL,
  PERMISSIONS.SIGN,
  PERMISSIONS.LOCK,
  PERMISSIONS.EXT_SIGN_LOCK,
  PERMISSIONS.RE_EVAL,
  PERMISSIONS.ADMIN,
  PERMISSIONS.USER_PROFILE,
  PERMISSIONS.USER_PROFILE_UPDATE_PASSWORD,
  PERMISSIONS.WOUND_ASSESSMENT_WITHOUT_PHOTO,
  PERMISSIONS.RISK_ASSESSMENT,
  PERMISSIONS.UNABLE_TO_ACCESS_WOUND,
  PERMISSIONS.REGISTER_PATIENT_FOR_MOBILE,
  PERMISSIONS.ASSESSMENT_NOTE_DELETE,
  PERMISSIONS.ASSESSMENT_NOTE_EDIT,
  PERMISSIONS.NOTIFICATION_EDIT,
  PERMISSIONS.ADT_EDIT,
  PERMISSIONS.ANATOMY_EDIT,
  PERMISSIONS.CHARACTERISTIC_HISTORY_EDIT,
  PERMISSIONS.CHARACTERISTIC_TISSUE_EDIT,
  PERMISSIONS.CHARACTERISTIC_TOUCH_EDIT,
  PERMISSIONS.CHARACTERISTIC_CUSTOM_FORM_EDIT,
  PERMISSIONS.CHARACTERISTIC_CLASSIFICATION_EDIT,
  PERMISSIONS.CHARACTERISTIC_PROGRESS_EDIT,
  PERMISSIONS.MEASUREMENT_EDIT,
  PERMISSIONS.TREATMENT_EDIT,
  PERMISSIONS.CUSTOMER_SALES_AREA_EDIT,
];

/** Every legacy key except EXT_SIGN_LOCK - verbatim port of the original ALL_REQUIRED_PERMISSION. */
const ALL_REQUIRED_PERMISSION = LEGACY_PERMISSION_KEYS.filter((key) => key !== PERMISSIONS.EXT_SIGN_LOCK);

/** Every legacy key except CUSTOMER_SALES_AREA_EDIT - verbatim port of the original EXTERNAL_USER_PERMISSION. */
const EXTERNAL_USER_PERMISSION = LEGACY_PERMISSION_KEYS.filter((key) => key !== PERMISSIONS.CUSTOMER_SALES_AREA_EDIT);

/** Verbatim port of the original EXTERNAL array shared by the three NE1 external roles. */
const EXTERNAL: PermissionKey[] = [
  PERMISSIONS.PATIENT_ROUTE,
  PERMISSIONS.REPORT_ROUTE,
  PERMISSIONS.PATIENT,
  PERMISSIONS.ADD_PATIENT,
  PERMISSIONS.WOUND_ASSESSMENT_WITHOUT_PHOTO,
  PERMISSIONS.RISK_ASSESSMENT,
  PERMISSIONS.UNABLE_TO_ACCESS_WOUND,
  PERMISSIONS.REGISTER_PATIENT_FOR_MOBILE,
  PERMISSIONS.ADT_EDIT,
  PERMISSIONS.RE_EVAL,
  PERMISSIONS.CUSTOM_LABEL,
  PERMISSIONS.ANATOMY_EDIT,
  PERMISSIONS.ASSESSMENT,
  PERMISSIONS.CHARACTERISTIC,
  PERMISSIONS.CHARACTERISTIC_HISTORY_EDIT,
  PERMISSIONS.CHARACTERISTIC_TISSUE_EDIT,
  PERMISSIONS.CHARACTERISTIC_TOUCH_EDIT,
  PERMISSIONS.CHARACTERISTIC_CUSTOM_FORM_EDIT,
  PERMISSIONS.CHARACTERISTIC_CLASSIFICATION_EDIT,
  PERMISSIONS.CHARACTERISTIC_PROGRESS_EDIT,
  PERMISSIONS.MEASUREMENT,
  PERMISSIONS.MEASUREMENT_BUTTON,
  PERMISSIONS.MEASUREMENT_EDIT,
  PERMISSIONS.TREATMENT,
  PERMISSIONS.TREATMENT_SEARCH,
  PERMISSIONS.TREATMENT_ADD_INTERVENTION,
  PERMISSIONS.TREATMENT_EDIT,
  PERMISSIONS.NOTES,
  PERMISSIONS.ASSESSMENT_NOTE_DELETE,
  PERMISSIONS.NOTIFICATION_EDIT,
  PERMISSIONS.USER_PROFILE,
  PERMISSIONS.USER_PROFILE_UPDATE_PASSWORD,
];

// ---------------------------------------------------------------------------
// New: granular /admin/* feature keys, by role. Derived from rbac.js's
// navRbac table (one admin feature area = one key, reused by nav/route/form
// alike), with the real narrowing agreed 2026-10-15:
//   - MedlineStandardUser / MedlineSupport narrowed to match their nav
//     visibility exactly (previously had full catch-all ADMIN access
//     regardless of what nav showed them).
//   - ExternalAdministrator narrowed to close Tier 0 #2 (previously could
//     reach all 12 admin sub-routes by direct URL despite nav hiding 8).
// These are intentionally separate from LEGACY_PERMISSION_KEYS/ADMIN/
// ADMIN_ROUTE above - the legacy catch-all keeps its EXACT original value
// per role so this file's diff stays additive-only. Phase 2 (routes) and
// Phase 3 (forms) have both finished migrating every call site onto these
// granular keys, so the narrowing below is now fully enforced; PERMISSIONS.ADMIN
// itself is dead (no remaining reader) and will be deleted, along with
// rbac.js, in the final cleanup commit.
// ---------------------------------------------------------------------------

const MEDLINE_ADMINISTRATOR_ADMIN_AREAS: PermissionKey[] = [
  PERMISSIONS.ADMIN_FORM_BUILDER,
  PERMISSIONS.ADMIN_IN_APP_MESSAGING,
  PERMISSIONS.ADMIN_MOBILE_APP_MGMT,
  PERMISSIONS.ADMIN_PLATFORM_ANALYTICS,
  PERMISSIONS.ADMIN_USAGE_ANALYTICS,
  PERMISSIONS.ADMIN_SURVEY_RESULT,
  PERMISSIONS.ADMIN_CONTEXTUAL_SUPPORT,
  PERMISSIONS.ADMIN_PRODUCTS,
  PERMISSIONS.ADMIN_CUSTOMERS,
  PERMISSIONS.ADMIN_FORMULARY,
  PERMISSIONS.ADMIN_PATIENTS,
  PERMISSIONS.ADMIN_USERS,
  PERMISSIONS.ADMIN_SSO,
  PERMISSIONS.ADMIN_DASHBOARD_SECTION,
  PERMISSIONS.ADMIN_MASTER_DATA_SECTION,
];

const MEDLINE_STANDARD_USER_ADMIN_AREAS: PermissionKey[] = [
  PERMISSIONS.ADMIN_FORMULARY,
  PERMISSIONS.ADMIN_MASTER_DATA_SECTION,
];

const MEDLINE_SUPPORT_ADMIN_AREAS: PermissionKey[] = [
  PERMISSIONS.ADMIN_PLATFORM_ANALYTICS,
  PERMISSIONS.ADMIN_USAGE_ANALYTICS,
  PERMISSIONS.ADMIN_PRODUCTS,
  PERMISSIONS.ADMIN_CUSTOMERS,
  PERMISSIONS.ADMIN_FORMULARY,
  PERMISSIONS.ADMIN_USERS,
  PERMISSIONS.ADMIN_PATIENTS,
  PERMISSIONS.ADMIN_CONTEXTUAL_SUPPORT,
  PERMISSIONS.ADMIN_DASHBOARD_SECTION,
  PERMISSIONS.ADMIN_MASTER_DATA_SECTION,
];

const EXTERNAL_ADMINISTRATOR_ADMIN_AREAS: PermissionKey[] = [
  PERMISSIONS.ADMIN_USAGE_ANALYTICS,
  PERMISSIONS.ADMIN_USERS,
  PERMISSIONS.ADMIN_PATIENTS,
  PERMISSIONS.ADMIN_CUSTOMERS,
  PERMISSIONS.ADMIN_DASHBOARD_SECTION,
];

// ---------------------------------------------------------------------------
// New: customer-action flags, by role. Verbatim port of rbac.js's
// customerRbac table - no narrowing decision was made for these, so values
// are unchanged from today (including MedlineAdministrator's existing empty
// set, which looks surprising but matches current production behavior).
// ---------------------------------------------------------------------------

const CUSTOMER_ACTIONS_FULL: PermissionKey[] = [
  PERMISSIONS.CUSTOMER_LIST_ALL,
  PERMISSIONS.CUSTOMER_IMPORT,
  PERMISSIONS.CUSTOMER_DELETE,
  PERMISSIONS.CUSTOMER_ADD,
  PERMISSIONS.CUSTOMER_BASIC_INFO_SAVE,
  PERMISSIONS.CUSTOMER_MORE_INFO_SAVE,
  PERMISSIONS.CUSTOMER_ORG_SAVE,
  PERMISSIONS.CUSTOMER_FORMULARY_SAVE,
  PERMISSIONS.CUSTOMER_SETTINGS_SAVE,
];

const EXTERNAL_ADMINISTRATOR_CUSTOMER_ACTIONS: PermissionKey[] = [
  PERMISSIONS.CUSTOMER_LIST_ALL,
  PERMISSIONS.CUSTOMER_IMPORT,
  PERMISSIONS.CUSTOMER_DELETE,
  PERMISSIONS.CUSTOMER_ADD,
  PERMISSIONS.CUSTOMER_BASIC_INFO_SAVE,
  PERMISSIONS.CUSTOMER_ORG_SAVE,
  PERMISSIONS.CUSTOMER_FORMULARY_SAVE,
];

export const ROLE_PERMISSIONS: Record<Role, PermissionKey[]> = {
  MedlineAdministrator: [...ALL_REQUIRED_PERMISSION, ...MEDLINE_ADMINISTRATOR_ADMIN_AREAS, PERMISSIONS.ASSESSMENT_EXTERNAL_OWN_CUSTOMER],
  MedlineStandardUser: [...ALL_REQUIRED_PERMISSION, ...MEDLINE_STANDARD_USER_ADMIN_AREAS, ...CUSTOMER_ACTIONS_FULL, PERMISSIONS.ASSESSMENT_EXTERNAL_OWN_CUSTOMER],
  MedlineSupport: [...ALL_REQUIRED_PERMISSION, ...MEDLINE_SUPPORT_ADMIN_AREAS, ...CUSTOMER_ACTIONS_FULL, PERMISSIONS.ASSESSMENT_EXTERNAL_OWN_CUSTOMER],
  ExternalAdministrator: [...EXTERNAL_USER_PERMISSION, ...EXTERNAL_ADMINISTRATOR_ADMIN_AREAS, ...EXTERNAL_ADMINISTRATOR_CUSTOMER_ACTIONS, PERMISSIONS.ASSESSMENT_EXTERNAL_OWN_CUSTOMER],
  ExternalStandardUser: [
    PERMISSIONS.WORKLIST_ROUTE,
    PERMISSIONS.PATIENT_ROUTE,
    PERMISSIONS.REPORT_ROUTE,
    PERMISSIONS.PATIENT,
    PERMISSIONS.ADD_PATIENT,
    PERMISSIONS.RISK_ASSESSMENT,
    PERMISSIONS.UNABLE_TO_ACCESS_WOUND,
    PERMISSIONS.ADT_EDIT,
    PERMISSIONS.RE_EVAL,
    PERMISSIONS.USER_PROFILE,
    PERMISSIONS.USER_PROFILE_UPDATE_PASSWORD,
    PERMISSIONS.MEASUREMENT,
    PERMISSIONS.MEASUREMENT_BUTTON,
    PERMISSIONS.MEASUREMENT_EDIT,
    PERMISSIONS.NOTES,
    PERMISSIONS.ASSESSMENT_NOTE_DELETE,
  ],
  ExternalNE1WoundExpert: [
    ...EXTERNAL,
    PERMISSIONS.WORKLIST_ROUTE,
    PERMISSIONS.TRANSFER_WOUND_ASSESSMENT_ROUTE,
    PERMISSIONS.MERGE_WOUND_ASSESSMENT_ROUTE,
    PERMISSIONS.ASSESSMENT_THREE_DOT_MENU,
    PERMISSIONS.LOCK,
    PERMISSIONS.SIGN,
    PERMISSIONS.ASSESSMENT_EXTERNAL_OWN_CUSTOMER,
  ],
  ExternalNE1Review: [...EXTERNAL, PERMISSIONS.WORKLIST_ROUTE, PERMISSIONS.EXT_SIGN_LOCK, PERMISSIONS.ASSESSMENT_EXTERNAL_OWN_CUSTOMER],
  ExternalNE1Assesment: [...EXTERNAL, PERMISSIONS.WORKLIST_ROUTE, PERMISSIONS.SIGN, PERMISSIONS.ASSESSMENT_EXTERNAL_OWN_CUSTOMER],
  ExternalNE1ReadOnly: [
    PERMISSIONS.PATIENT_ROUTE,
    PERMISSIONS.REPORT_ROUTE,
    PERMISSIONS.PATIENT,
    PERMISSIONS.ASSESSMENT,
    PERMISSIONS.CHARACTERISTIC,
    PERMISSIONS.MEASUREMENT,
    PERMISSIONS.MEASUREMENT_BUTTON,
    PERMISSIONS.TREATMENT,
    PERMISSIONS.NOTES,
    PERMISSIONS.USER_PROFILE,
    PERMISSIONS.USER_PROFILE_UPDATE_PASSWORD,
    PERMISSIONS.ASSESSMENT_EXTERNAL_OWN_CUSTOMER,
  ],
};
